import React, { useState, useEffect } from "react";

const COMPACT_MQ = "(max-width: 768px)";

function getVisiblePages(totalPages, currentPage, compact) {
    if (!totalPages || totalPages <= 0) {
        return [];
    }

    if (compact) {
        if (totalPages <= 3) {
            return Array.from({ length: totalPages }, (_, i) => i + 1);
        }
        const start = Math.max(1, Math.min(currentPage - 1, totalPages - 2));
        const end = Math.min(totalPages, start + 2);
        return Array.from({ length: end - start + 1 }, (_, i) => start + i);
    }

    if (totalPages <= 5) {
        return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const pages = [1];
    const leftPage = Math.max(2, currentPage - 1);
    const rightPage = Math.min(totalPages - 1, currentPage + 1);

    if (leftPage > 2) {
        pages.push("left-ellipsis");
    }

    for (let page = leftPage; page <= rightPage; page++) {
        pages.push(page);
    }

    if (rightPage < totalPages - 1) {
        pages.push("right-ellipsis");
    }

    pages.push(totalPages);
    return pages;
}

// ponytail: one check — fails if compact window drifts off current page
if (typeof process !== "undefined" && process.env && process.env.NODE_ENV === "development") {
    const sample = getVisiblePages(90, 4, true);
    console.assert(
        sample.length === 3 && sample[0] === 3 && sample[1] === 4 && sample[2] === 5,
        "PaginationBar compact window expected [3,4,5]"
    );
}

const Pagination = (props) => {
    const {
        totalPages,
        retrieveDataRecords,
        searchKeyword,
        isResetCurrentPage,
        setIsChangeTab,
        onPageChange,
        currentPageProp
    } = props;

    const [currentPage, setCurrentPage] = useState(1);
    const [compact, setCompact] = useState(() =>
        typeof window !== "undefined" &&
        typeof window.matchMedia === "function" &&
        window.matchMedia(COMPACT_MQ).matches
    );

    useEffect(() => {
        if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
            return;
        }
        const mq = window.matchMedia(COMPACT_MQ);
        const onChange = () => setCompact(mq.matches);
        onChange();
        if (mq.addEventListener) {
            mq.addEventListener("change", onChange);
            return () => mq.removeEventListener("change", onChange);
        }
        mq.addListener(onChange);
        return () => mq.removeListener(onChange);
    }, []);

    useEffect(() => {
        if (isResetCurrentPage) {
            resetCurrentPage();
            setIsChangeTab(false);
        }
    }, [isResetCurrentPage, setIsChangeTab]);

    useEffect(() => {
        // set current page on change loading
        if (currentPageProp && currentPage !== currentPageProp) {
            setCurrentPage(currentPageProp);
        }
    }, [currentPageProp, currentPage]);

    useEffect(() => {
        if (typeof onPageChange === "function") {
            onPageChange(currentPage);
        }
    }, [currentPage, onPageChange]);

    const goToPage = (pageNumber) => {
        if (pageNumber >= 1 && pageNumber <= totalPages) {
            setCurrentPage(pageNumber);

            if (retrieveDataRecords) {
                retrieveDataRecords(pageNumber, searchKeyword);
            }
        }
    };

    const handleEllipsisClick = (ellipsisType) => {
        const jumpSize = 2;

        if (ellipsisType === "left-ellipsis") {
            goToPage(Math.max(1, currentPage - jumpSize));
            return;
        }

        goToPage(Math.min(totalPages, currentPage + jumpSize));
    };

    function resetCurrentPage() {
        setCurrentPage(1);
    }

    const visiblePages = getVisiblePages(totalPages, currentPage, compact);
    const showFirst = !visiblePages.includes(1);
    const showLast = totalPages > 0 && !visiblePages.includes(totalPages);

    return (
        <div className="pagination-bar">
            {showFirst && (
                <button className="arrow-btn" onClick={() => goToPage(1)} aria-label="First page">
                    &#171;
                </button>
            )}
            <div className="pagination-number-bar">
                {visiblePages.map((page, index) =>
                    typeof page === "number" ? (
                        <button
                            key={page}
                            className={"page-btn" + (currentPage === page ? " active" : "")}
                            onClick={() => goToPage(page)}
                        >
                            {page}
                        </button>
                    ) : (
                        <button
                            key={`${page}-${index}`}
                            className="page-btn"
                            onClick={() => handleEllipsisClick(page)}
                            aria-label={page === "left-ellipsis" ? "Jump backward pages" : "Jump forward pages"}
                        >
                            ...
                        </button>
                    )
                )}
            </div>
            {showLast && (
                <button className="arrow-btn" onClick={() => goToPage(totalPages)} aria-label="Last page">
                    &#187;
                </button>
            )}
        </div>
    );
};

export default Pagination;
